import React, { useMemo } from "react";

import { ICellRendererParams } from "@ag-grid-community/core";
import { faMapMarkedAlt } from "@fortawesome/pro-light-svg-icons/faMapMarkedAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";

import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";

import { LocationMiniMapEntity } from "@app/component/field/LocationMiniMap";
import CheckInLocationMiniMap from "@app/component/field/LocationMiniMap/CheckInLocationMiniMap";
import RecordLocationMiniMap from "@app/component/field/LocationMiniMap/RecordLocationMiniMap";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { formatCoordinates } from "@app/util/formatters";

import styles from "./LocationCellRenderer.module.scss";

const LocationCellRenderer: React.FC<ICellRendererParams> = ({ colDef, data, value }) => {
  const mappedEntity: LocationMiniMapEntity = useMemo(() => {
    if (data.geoPoint) {
      return data;
    }
    if (data.account?.geoPoint) {
      return data.account;
    }
    if (data.contact?.geoPoint) {
      return data.contact;
    }
  }, [data]);

  if (!mappedEntity || !value) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  const coordinates = value as GeoPoint["coordinates"];

  return (
    <div className={styles.container}>
      <Popover
        content={
          colDef.colId === ActivityFieldName.GEOPOINT_CHECK_IN && data.geoPointCheckin ? (
            <CheckInLocationMiniMap
              className={styles.map}
              coordinates={data.geoPointCheckin.coordinates}
            />
          ) : (
            <RecordLocationMiniMap className={styles.map} entity={mappedEntity} />
          )
        }
        destroyTooltipOnHide
        placement="right"
        // transition name must be empty, otherwise map or map controls might be not displayed:
        // https://stackoverflow.com/a/64416167/5346779
        transitionName=""
      >
        <FontAwesomeIcon className={styles.icon} icon={faMapMarkedAlt} />
        {formatCoordinates(coordinates)}
      </Popover>
    </div>
  );
};

export default LocationCellRenderer;
